import React, { useEffect, useState } from 'react';
import marketApi from './marketApi';
import { useCart } from './CartContext';

/**
 * Pharmacie (web) — « Envoyer mon ordonnance » et « Mes devis ».
 * Mêmes règles que l'app native : la pharmacie compose le devis depuis son
 * catalogue, le client le valide puis paie (articles fixés).
 */

const MAX_PHOTOS = 5;

/** Sélection de photos d'ordonnance (fichiers image, aperçu, retrait). */
export function PrescriptionFiles({ files, onChange }) {
  const add = (e) => {
    const picked = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    e.target.value = '';
    onChange([...files, ...picked].slice(0, MAX_PHOTOS));
  };
  return (
    <div>
      {files.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {files.map((f, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={URL.createObjectURL(f)} alt="ordonnance" style={{ width: 84, height: 108, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--mb-border)' }} />
              <button onClick={() => onChange(files.filter((_, j) => j !== i))} aria-label="Retirer"
                style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: 11, border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', cursor: 'pointer' }}>×</button>
            </div>
          ))}
        </div>
      )}
      {files.length < MAX_PHOTOS && (
        <label className="mk-btn ghost" style={{ cursor: 'pointer' }}>
          📷 Ajouter une photo de l'ordonnance
          <input type="file" accept="image/*" multiple onChange={add} style={{ display: 'none' }} />
        </label>
      )}
      <p className="mk-note" style={{ marginTop: 10 }}>🔒 Votre ordonnance n'est visible que par la pharmacie (jamais par le livreur) et supprimée automatiquement après quelques mois.</p>
    </div>
  );
}

export function RxQuotePage({ supplierId, supplierName, nav, isAuthenticated }) {
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const send = async () => {
    setError(null);
    if (!isAuthenticated) { setError('Connectez-vous pour envoyer votre ordonnance.'); return; }
    if (!files.length) { setError('Ajoutez au moins une photo de votre ordonnance.'); return; }
    setSending(true);
    try {
      const photo_ids = await marketApi.uploadPrescriptions(files);
      const r = await marketApi.requestQuote({ supplier_id: supplierId, photo_ids, note: note.trim() });
      if (!r?.success) throw new Error(r?.error || 'Envoi impossible');
      setDone(true);
    } catch (e) { setError(e?.error || e?.message || 'Envoi impossible'); }
    finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="mk-container">
        <div className="mkh-empty">
          <span className="big">📨</span>
          <b>Ordonnance envoyée</b>
          <p>La pharmacie prépare votre devis. Retrouvez-le dans « Mes devis » dès qu'il est prêt.</p>
          <button className="mk-btn primary" style={{ marginTop: 12 }} onClick={() => nav({ page: 'my-quotes' })}>Voir mes devis</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mk-container" style={{ maxWidth: 640 }}>
      <div className="mk-panel" style={{ marginTop: 16 }}>
        <h3>Envoyer mon ordonnance{supplierName ? ` — ${supplierName}` : ''}</h3>
        <p className="mk-desc">
          Photographiez votre ordonnance : le pharmacien vous prépare un devis avec les produits prescrits
          (ou leurs équivalents génériques, clairement indiqués). Vous le validez ensuite et payez.
        </p>
        <PrescriptionFiles files={files} onChange={setFiles} />
        <div className="mk-field" style={{ marginTop: 14 }}>
          <label>Message pour le pharmacien (facultatif)</label>
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} maxLength={500}
            placeholder="Ex. Je préfère les génériques, allergie à la pénicilline…" />
        </div>
        {error && <p className="mk-note err">{error}</p>}
        <button className="mk-btn primary block" style={{ marginTop: 10 }} disabled={sending} onClick={send}>
          {sending ? 'Envoi…' : 'Envoyer à la pharmacie'}
        </button>
      </div>
    </div>
  );
}

const STATUS = {
  requested: ['⏳ En préparation', '#B45309', '#FEF3C7'],
  quoted: ['💊 Devis prêt', '#1D4ED8', '#EFF6FF'],
  ordered: ['✅ Commandé', '#047857', '#ECFDF5'],
  declined: ['❌ Refusé', '#B91C1C', '#FEE2E2'],
  cancelled: ['Annulé', '#6B7280', '#F3F4F6'],
  expired: ['⌛ Expiré', '#6B7280', '#F3F4F6'],
};
const remaining = (iso) => {
  const m = Math.round((new Date(iso) - Date.now()) / 60000);
  if (m <= 0) return 'expiré';
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
};

export function MyQuotesPage({ nav }) {
  const { cart, setCartFromQuote } = useCart();
  const [quotes, setQuotes] = useState(null);

  const load = () => marketApi.myQuotes().then(r => setQuotes(r?.quotes || [])).catch(() => setQuotes([]));
  useEffect(() => { load(); }, []);

  const accept = (q) => {
    if (cart?.items?.length && cart.quote_id !== q.quote_id
      && !window.confirm('Votre panier actuel sera remplacé par ce devis. Continuer ?')) return;
    setCartFromQuote(q);
    nav({ page: 'checkout' });
  };
  const cancel = async (q) => {
    if (!window.confirm('Annuler cette demande de devis ?')) return;
    await marketApi.cancelQuote(q.quote_id).catch(() => {});
    load();
  };

  if (quotes === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;

  return (
    <div className="mk-container" style={{ maxWidth: 760 }}>
      {quotes.length === 0 && (
        <div className="mkh-empty">
          <span className="big">💊</span>
          <b>Aucun devis pour l'instant</b>
          <p>Depuis la page d'une pharmacie, cliquez sur « Envoyer mon ordonnance ».</p>
        </div>
      )}
      {quotes.map(q => {
        const st = STATUS[q.expired ? 'expired' : q.status] || STATUS.requested;
        const total = (q.lines || []).reduce((s, l) => s + (l.unit_price || 0) * l.quantity, 0);
        const live = q.status === 'quoted' && !q.expired;
        return (
          <div className="mk-panel" key={q.quote_id} style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0, flex: 1 }}>{q.supplier?.business_name || 'Pharmacie'}</h3>
              <span style={{ background: st[2], color: st[1], borderRadius: 8, padding: '3px 10px', fontSize: '.78rem', fontWeight: 800 }}>{st[0]}</span>
            </div>
            {q.status === 'declined' && <p className="mk-note err" style={{ marginTop: 10 }}>{q.decline_reason}</p>}
            {q.status === 'quoted' && (
              <>
                {(q.lines || []).map(l => (
                  <div className="mk-total" key={l.product_id}>
                    <span>
                      {l.quantity}× {l.name}
                      {l.is_generic && <div style={{ fontSize: '.76rem', color: '#1D4ED8' }}>Générique — remplace « {l.generic_for} »</div>}
                      {l.rx_original_return && <div style={{ fontSize: '.76rem', color: '#1D4ED8' }}>📄 Ordonnance originale à remettre au livreur</div>}
                    </span>
                    <span>{((l.unit_price || 0) * l.quantity).toLocaleString('fr-FR')} FC</span>
                  </div>
                ))}
                {q.unavailable_note && <p className="mk-note" style={{ marginTop: 8 }}>Indisponible : {q.unavailable_note}</p>}
                {q.pharmacist_note && <p className="mk-note" style={{ marginTop: 8 }}>💬 {q.pharmacist_note}</p>}
                <div className="mk-total grand"><span>Produits (+ livraison)</span><span>{total.toLocaleString('fr-FR')} FC</span></div>
                {live && <p style={{ textAlign: 'right', color: '#B45309', fontWeight: 700, fontSize: '.85rem', margin: '4px 0 0' }}>⏱ Valable encore {remaining(q.expires_at)}</p>}
              </>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              {live && <button className="mk-btn primary" style={{ flex: 1 }} onClick={() => accept(q)}>Valider et payer</button>}
              {['requested', 'quoted'].includes(q.status) && !q.expired && (
                <button className="mk-btn ghost" onClick={() => cancel(q)}>Annuler</button>
              )}
              {q.status === 'ordered' && q.order_id && (
                <button className="mk-btn ghost" onClick={() => nav({ page: 'order', orderId: q.order_id })}>Suivre la commande</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
